import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type OpsCancelInput, type OpsRideRow } from '@vello/api-client';
import { type DispatchQueue, type DispatchLane, type OperatorRideTimeline, laneForStatus, loadDispatchQueue, issueDispatchWave, manualDispatch, cancelRide, loadRideTimeline, formatMarketInstant, marketTimeZone, marketDisplayName, tailOf } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, CheckRow, Drawer, DrawerBody, DrawerFooter, DrawerHeader, Field, Notice, PageHeader, Panel, StatCard, Table, TextInput, FormRow, EmptyState, SectionTitle, Grid, Ink, Text, EmptyCell, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import { PersonName, usePersonName, usePersonNames } from '../components/person-name';
import { DrawerStack } from '../components/drawer-stack';
import { IdRef } from '../components/code-ref';
import { RideCancelDialog } from '../components/ride-cancel-dialog';

/**
 * Despacho (D318) — the operator dispatch console over the real S092/S112 surface: the scoped active-ride queue
 * (`GET /v1/ops/dashboard`, laned by dispatch state), a ride's durable timeline, nearby candidates
 * (`GET /v1/dispatch/rides/:id/candidates`, scoped by the server to the ride's market), an audited **manual
 * assignment** (`POST …/manual`, operator AAL2 `dispatch:override` — the server validates the driver's
 * eligibility attestation before the election), a **dispatch wave** to the drivers the operator picked
 * (`POST …/offers`), and an operator **cancel with a reason code** (`POST /v1/ops/rides/:id/cancel`, through the
 * {@link RideCancelDialog}: the agent's optional note is internal and sent apart — W5-HOTFIX, D382). Every action
 * carries a reason (REQ-ADM-07). No fabricated rows: the queue is empty until the feed returns.
 */
interface Candidate {
  readonly driverId: string;
  readonly distanceMeters?: number;
  readonly etaSeconds?: number;
}

function asCandidate(raw: unknown): Candidate | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const c = raw as Record<string, unknown>;
  if (typeof c.driverId !== 'string') return undefined;
  return {
    driverId: c.driverId,
    ...(typeof c.distanceMeters === 'number' ? { distanceMeters: c.distanceMeters } : {}),
    ...(typeof c.etaSeconds === 'number' ? { etaSeconds: c.etaSeconds } : {}),
  };
}

const LANE_TONE: Readonly<Record<DispatchLane, 'neutral' | 'success' | 'warning' | 'danger'>> = {
  searching: 'warning',
  assigned: 'success',
  inProgress: 'neutral',
  other: 'neutral',
};

const EMPTY_QUEUE: DispatchQueue = { searching: [], assigned: [], inProgress: [], other: [], total: 0 };

export function DispatchScreen(): ReactElement {
  const { runtime, scope, markets } = useAdmin();
  const { t, tp, lang } = useT();
  const { fail, v } = useVocab();
  const [queue, setQueue] = useState<DispatchQueue | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [refresh, setRefresh] = useState(0);

  const [selected, setSelected] = useState<OpsRideRow | undefined>(undefined);
  const [timeline, setTimeline] = useState<OperatorRideTimeline | undefined>(undefined);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [radius, setRadius] = useState('3000');
  const [candidates, setCandidates] = useState<readonly Candidate[]>([]);
  const candidateName = usePersonNames(candidates.map((c) => c.driverId));
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger' | 'warning'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | undefined>(undefined);

  // A background tick bumps `refresh` with `quiet` set: the queue stays on screen (no loading block) while it is re-read.
  const quiet = useRef(false);
  useEffect(() => {
    let cancelled = false;
    if (!quiet.current) setLoading(true);
    quiet.current = false;
    setError(undefined);
    void loadDispatchQueue(runtime, scope.current, markets).then((res) => {
      if (cancelled) return;
      if (res.ok) setQueue(res.value);
      else {
        setQueue(EMPTY_QUEUE);
        setError(t('disp.error.queue', { code: fail(res.error) }));
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, scope, markets, refresh, t]);
  useAutoRefresh(() => {
    quiet.current = true;
    setRefresh((n) => n + 1);
  }, { intervalMs: AUTO_REFRESH_MS.live });

  const open = useCallback(
    async (ride: OpsRideRow): Promise<void> => {
      setSelected(ride);
      setTimeline(undefined);
      setCandidates([]);
      setPicked(new Set());
      setMsg(undefined);
      const tl = await loadRideTimeline(runtime, ride.rideId);
      setTimeline(tl.ok ? tl.value : undefined);
    },
    [runtime],
  );

  const findCandidates = async (): Promise<void> => {
    if (selected === undefined) return;
    setMsg(undefined);
    setBusy(true);
    const res = await runtime.ops.candidates(selected.rideId, { latitude: Number(lat), longitude: Number(lng), radiusMeters: Number(radius) || 3000, limit: 20 });
    setBusy(false);
    if (res.ok) {
      const list = res.value.candidates.map(asCandidate).filter((c): c is Candidate => c !== undefined);
      setCandidates(list);
      setPicked(new Set());
      if (list.length === 0) setMsg({ tone: 'warning', text: t('disp.candidates.none') });
    } else {
      setCandidates([]);
      setMsg({ tone: 'danger', text: t('disp.error.candidates', { code: fail(res.error) }) });
    }
  };

  const assign = async (driverId: string): Promise<void> => {
    if (selected === undefined) return;
    setBusy(true);
    const res = await manualDispatch(runtime, selected.rideId, { driverId, reason: reason.trim() });
    setBusy(false);
    if (res.ok) {
      setMsg({ tone: 'success', text: t('disp.assigned', { driver: candidateName(driverId) ?? tailOf(driverId) }) });
      setRefresh((n) => n + 1);
    } else {
      setMsg({ tone: 'danger', text: t('disp.error.assign', { code: fail(res.error) }) });
    }
  };

  const sendWave = async (): Promise<void> => {
    if (selected === undefined || picked.size === 0) return;
    setBusy(true);
    const res = await issueDispatchWave(runtime, selected.rideId, [...picked]);
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: t('disp.error.wave', { code: fail(res.error) }) });
      return;
    }
    if (res.value.noDriver === true || res.value.offers.length === 0) setMsg({ tone: 'warning', text: t('disp.wave.noDriver') });
    else setMsg({ tone: 'success', text: tp('disp.wave.sent', res.value.offers.length, { wave: res.value.waveNumber }) });
    setRefresh((n) => n + 1);
  };

  // W5-HOTFIX (D382): the cancel takes a reason code from the dialog; the free-text field above is the manual-dispatch
  // reason only — it is never sent as a cancellation reason (that is how an agent's note used to reach the rider).
  const cancel = async (input: OpsCancelInput): Promise<void> => {
    if (selected === undefined) return;
    setBusy(true);
    setCancelError(undefined);
    const res = await cancelRide(runtime, selected.rideId, input);
    setBusy(false);
    if (res.ok) {
      setCancelOpen(false);
      setMsg({ tone: 'success', text: t('disp.cancelled') });
      setRefresh((n) => n + 1);
    } else {
      setCancelError(t('disp.error.cancel', { code: fail(res.error) }));
    }
  };

  const q = queue ?? EMPTY_QUEUE;
  const rows: readonly OpsRideRow[] = [...q.searching, ...q.assigned, ...q.inProgress, ...q.other];
  const hasReason = reason.trim() !== '';
  const selectedDriverName = usePersonName(selected?.driverId ?? undefined);
  const hasPoint = lat.trim() !== '' && lng.trim() !== '' && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));

  return (
    <>
      <PageHeader title={t('disp.title')} subtitle={t('disp.subtitle')} />

      {error !== undefined && (
        <div>
          <Notice tone="danger">{error}</Notice>
        </div>
      )}

      <Grid min="sm" gap={4}>
        <StatCard label={t('disp.kpi.searching')} value={queue ? String(q.searching.length) : '—'} hint={t('disp.kpi.searching.hint')} />
        <StatCard label={t('disp.kpi.assigned')} value={queue ? String(q.assigned.length) : '—'} />
        <StatCard label={t('disp.kpi.inProgress')} value={queue ? String(q.inProgress.length) : '—'} />
        <StatCard label={t('disp.kpi.total')} value={queue ? String(q.total) : '—'} />
      </Grid>

      <Panel padded={false}>
        <Table
          columns={[
            { key: 'ride', header: t('disp.col.ride'), render: (r: OpsRideRow) => (r.code !== undefined ? <span className="tnum" title={r.rideId}>{r.code}</span> : <IdRef value={r.rideId} />) },
            { key: 'lane', header: t('disp.col.lane'), render: (r: OpsRideRow) => <Badge tone={LANE_TONE[laneForStatus(r.status)]}>{t(`disp.lane.${laneForStatus(r.status)}`)}</Badge> },
            { key: 'status', header: t('disp.col.status'), render: (r: OpsRideRow) => v('ride.status', r.status) },
            ...(scope.current.city === undefined ? [{ key: 'city', header: t('disp.col.city'), render: (r: OpsRideRow) => marketDisplayName(r.city, markets) }] : []),
            { key: 'driver', header: t('disp.col.driver'), render: (r: OpsRideRow) => <PersonName id={r.driverId} /> },
            { key: 'category', header: t('disp.col.category'), render: (r: OpsRideRow) => v('ride.category', r.categoryCode) },
            { key: 'created', header: t('disp.col.created'), render: (r: OpsRideRow) => <span className="tnum">{formatMarketInstant(r.createdAt, marketTimeZone(r.city, markets), lang, 'time')}</span> },
          ]}
          rows={rows}
          rowKey={(r) => r.rideId}
          onRowClick={(r) => void open(r)}
          {...(selected !== undefined ? { selectedKey: selected.rideId } : {})}
          loading={loading}
          loadingLabel={t('ui.loading')}
          empty={<EmptyState title={t('disp.queue.empty')} />}
        />
      </Panel>

      <Drawer open={selected !== undefined} onClose={() => setSelected(undefined)} labelledBy="disp-detail-title">
        {selected !== undefined && (
          <>
            <DrawerHeader
              titleId="disp-detail-title"
              title={selected.code !== undefined ? <span className="tnum" title={selected.rideId}>{selected.code}</span> : <IdRef value={selected.rideId} copy full />}
              subtitle={
                <>
                  <Badge tone={LANE_TONE[laneForStatus(selected.status)]}>{v('ride.status', selected.status)}</Badge>
                  {timeline !== undefined && (
                    <>
                      {' '}
                      <Badge tone="neutral">{v('ride.status', timeline.status)}</Badge> <Text as="span" role="caption" tone="tertiary" mono>{tp('disp.events', timeline.events.length)}</Text>
                    </>
                  )}
                </>
              }
              onClose={() => setSelected(undefined)}
              closeLabel={t('ui.close')}
            />
            <DrawerBody>
              <DrawerStack>
              <Text as="p" role="caption" tone="secondary">
                {t('disp.detail.meta', { city: marketDisplayName(selected.city, markets), driver: selectedDriverName ?? '—', category: v('ride.category', selected.categoryCode) })}
              </Text>

              <Field label={t('ui.reason')} htmlFor="disp-reason" hint={t('disp.reason.hint')}>
                <TextInput id="disp-reason" value={reason} placeholder={t('ui.reason.placeholder')} onChange={(e) => setReason(e.target.value)} />
              </Field>

              <SectionTitle level={3} size="sub">{t('disp.candidates.title')}</SectionTitle>
              <FormRow
                action={
                  <Button variant="secondary" disabled={busy || !hasPoint} onClick={() => void findCandidates()}>{t('disp.findCandidates')}</Button>
                }
              >
                <Field label={t('disp.field.lat')} htmlFor="disp-lat">
                  <TextInput id="disp-lat" className="tnum" inputMode="decimal" value={lat} onChange={(e) => setLat(e.target.value)} />
                </Field>
                <Field label={t('disp.field.lng')} htmlFor="disp-lng">
                  <TextInput id="disp-lng" className="tnum" inputMode="decimal" value={lng} onChange={(e) => setLng(e.target.value)} />
                </Field>
                <Field label={t('disp.field.radius')} htmlFor="disp-radius" hint={t('disp.field.radius.hint')}>
                  <TextInput id="disp-radius" className="tnum" inputMode="numeric" value={radius} onChange={(e) => setRadius(e.target.value)} />
                </Field>
              </FormRow>

              {msg !== undefined && (
                <div>
                  <Notice tone={msg.tone}>{msg.text}</Notice>
                </div>
              )}

              <Table
                columns={[
                  {
                    key: 'pick',
                    header: t('disp.col.pick'),
                    render: (c: Candidate) => (
                      <CheckRow
                        id={`pick-${c.driverId}`}
                        checked={picked.has(c.driverId)}
                        onChange={(on) =>
                          setPicked((prev) => {
                            const next = new Set(prev);
                            if (on) next.add(c.driverId);
                            else next.delete(c.driverId);
                            return next;
                          })
                        }
                      >
                        <PersonName id={c.driverId} />
                      </CheckRow>
                    ),
                  },
                  { key: 'dist', header: t('disp.col.distance'), numeric: true, render: (c: Candidate) => (c.distanceMeters !== undefined ? `${Math.round(c.distanceMeters)} m` : '—') },
                  { key: 'eta', header: t('disp.col.eta'), numeric: true, render: (c: Candidate) => (c.etaSeconds !== undefined ? `${Math.round(c.etaSeconds / 60)} min` : '—') },
                  {
                    key: 'act',
                    header: '',
                    render: (c: Candidate) => (
                      <Ink as="div" align="end" block>
                        <Button variant="ghost" disabled={busy || !hasReason} onClick={() => void assign(c.driverId)}>{t('disp.assign')}</Button>
                      </Ink>
                    ),
                  },
                ]}
                rows={candidates}
                rowKey={(c) => c.driverId}
                empty={<EmptyCell>{t('disp.candidates.empty')}</EmptyCell>}
              />
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="danger" disabled={busy} onClick={() => { setCancelError(undefined); setCancelOpen(true); }}>{t('disp.cancelRide')}</Button>
              <Button variant="primary" disabled={busy || picked.size === 0} onClick={() => void sendWave()}>
                {tp('disp.sendWave', picked.size)}
              </Button>
            </DrawerFooter>
          </>
        )}
      </Drawer>

      <RideCancelDialog
        open={cancelOpen && selected !== undefined}
        rideLabel={selected?.code ?? selected?.rideId}
        busy={busy}
        error={cancelError}
        onCancel={() => setCancelOpen(false)}
        onConfirm={(input) => void cancel(input)}
      />
    </>
  );
}
